import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, FileSpreadsheet, FileText, Info } from "lucide-react";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { downloadExcel, downloadPdf } from "./plannerApi";
import { plannerTrack } from "./analytics";
import { formatCompact, formatINR } from "./format";
import { SCENARIO_PALETTE } from "./ScenarioTabs";

const Stat = ({ label, value, accent, testId, tip }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white px-5 py-4" data-testid={testId}>
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body">
            <span>{label}</span>
            {tip && (
                <span className="relative group inline-flex" data-testid={`${testId}-tip`}>
                    <Info size={12} className="text-oth-ink3 cursor-help" />
                    <span className="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-72 normal-case tracking-normal text-[12px] leading-snug bg-[#0A0A0A] text-white rounded-xl px-3 py-2 shadow-lg font-body">
                        {tip}
                    </span>
                </span>
            )}
        </div>
        <div className={`font-display text-xl md:text-2xl font-semibold tracking-tight mt-1 ${accent || ""}`}>{value}</div>
    </div>
);

const th = "px-3 py-2.5 text-right whitespace-nowrap font-medium text-[11px] uppercase tracking-[0.14em]";
const td = "px-3 py-2 text-right whitespace-nowrap tabular-nums";

const ExportBar = ({ buildPayload, planCode, planSaved, multi }) => {
    const [busy, setBusy] = useState("");
    const go = async (kind) => {
        setBusy(kind);
        try {
            const payload = { ...buildPayload(), plan_code: planCode || "" };
            await (kind === "excel" ? downloadExcel(payload) : downloadPdf(payload));
            plannerTrack(kind === "excel" ? "excel_download" : "pdf_download");
            toast.success(kind === "excel" ? "Excel downloaded." : "PDF downloaded.");
        } catch (e) {
            console.error("export failed", e);
            toast.error("Download failed. Please check your inputs and try again.");
        } finally { setBusy(""); }
    };
    if (!buildPayload) return null;
    return (
        <div className="rounded-2xl border border-oth-line/70 bg-white px-5 py-4 flex items-center justify-between flex-wrap gap-3" data-testid="export-bar">
            <div className="text-[13px] font-body text-oth-ink3 leading-snug max-w-xl">
                {multi ? "Download every scenario together — one comparison plus a sheet/section per scenario." : "Take your plan with you."}{" "}
                {!planSaved && <span className="text-[#9c6a1c]">Downloading does not save your plan — if you don't save it with a secret, it will be lost when you leave this page.</span>}
            </div>
            <div className="flex items-center gap-2">
                <button onClick={() => go("excel")} disabled={!!busy} className="btn-pill btn-pill-light text-sm disabled:opacity-50" data-testid="download-excel-btn">
                    <FileSpreadsheet size={14} /> {busy === "excel" ? "Preparing…" : "Download Excel"}
                </button>
                <button onClick={() => go("pdf")} disabled={!!busy} className="btn-pill btn-pill-dark text-sm disabled:opacity-50" data-testid="download-pdf-btn">
                    <FileText size={14} /> {busy === "pdf" ? "Preparing…" : "Download PDF"}
                </button>
            </div>
        </div>
    );
};

const COMPARISON_METRICS = [
    { key: "starting_wealth", label: "Starting Wealth", fmt: formatCompact },
    { key: "weighted_return", label: "Weighted Return", fmt: (v) => `${Number(v).toFixed(2)}%` },
    { key: "growth_rate", label: "Growth Rate Used", fmt: (v) => `${v}%` },
    { key: "final_wealth", label: "Projected Wealth", fmt: formatCompact, neg: true },
    { key: "final_wealth_pv", label: "Present Value", fmt: formatCompact, neg: true },
    { key: "final_risk_free_monthly", label: "Lifestyle Sustainability", fmt: formatCompact },
    { key: "first_negative_year", label: "Turns Negative", fmt: (v) => (v ? String(v) : "Never") },
];

const ComparisonCard = ({ scenarios }) => {
    // Merge closing wealth by year across all scenarios for an overlaid chart.
    const yearsSet = new Set();
    scenarios.forEach((s) => (s.projection.rows || []).forEach((r) => yearsSet.add(r.year)));
    const years = Array.from(yearsSet).sort((a, b) => a - b);
    const byScenario = scenarios.map((s) => {
        const m = {};
        (s.projection.rows || []).forEach((r) => { m[r.year] = r.closing; });
        return m;
    });
    const chartData = years.map((y) => {
        const point = { year: y };
        scenarios.forEach((s, i) => { point[`s${i}`] = byScenario[i][y]; });
        return point;
    });

    return (
        <div className="rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8 space-y-6" data-testid="scenario-comparison-card">
            <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Scenario comparison</h3>
                <p className="text-sm text-oth-ink3 font-body mt-1">How each scenario's closing wealth tracks over time, and where they land.</p>
            </div>

            <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e3dd" />
                        <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6b6b67" }} tickLine={false} axisLine={false} />
                        <YAxis tickFormatter={(v) => formatCompact(v)} tick={{ fontSize: 11, fill: "#6b6b67" }} width={72} tickLine={false} axisLine={false} />
                        <ChartTooltip formatter={(v, n) => [formatCompact(v), scenarios[Number(n.slice(1))]?.name || n]} labelFormatter={(y) => `Year ${y}`} contentStyle={{ borderRadius: 12, border: "1px solid #d9d7d1", fontSize: 12 }} />
                        <Legend formatter={(n) => scenarios[Number(n.slice(1))]?.name || n} wrapperStyle={{ fontSize: 12 }} />
                        {scenarios.map((s, i) => (
                            <Line key={s.id} type="monotone" dataKey={`s${i}`} name={`s${i}`} stroke={SCENARIO_PALETTE[i % SCENARIO_PALETTE.length]} strokeWidth={2} dot={false} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto thin-scroll">
                <table className="text-[13px] font-body border-collapse min-w-full" data-testid="scenario-comparison-table">
                    <thead>
                        <tr className="bg-[#f0f0ec]">
                            <th className="px-3 py-2.5 text-left font-medium text-[11px] uppercase tracking-[0.14em] sticky left-0 z-10 bg-[#f0f0ec]">Metric</th>
                            {scenarios.map((s, i) => (
                                <th key={s.id} className={`${th} text-oth-ink`}>
                                    <span className="inline-flex items-center gap-1.5 justify-end">
                                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SCENARIO_PALETTE[i % SCENARIO_PALETTE.length] }} />
                                        {s.name}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {COMPARISON_METRICS.map((m) => (
                            <tr key={m.key} className="border-b border-oth-line/40" data-testid={`comparison-row-${m.key}`}>
                                <td className="px-3 py-2 text-left text-oth-ink2 sticky left-0 z-[1] bg-white">{m.label}</td>
                                {scenarios.map((s) => {
                                    const val = s.projection.summary[m.key];
                                    const isNeg = m.neg && Number(val) < 0;
                                    return <td key={s.id} className={`${td} ${isNeg ? "text-red-600 font-semibold" : ""}`}>{m.fmt(val)}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function ScenarioProjection({ projection }) {
    const { summary, rows, columns, child_columns: childCols } = projection;
    const neg = summary.first_negative_year;
    const chartData = rows.map((r) => ({ year: r.year, closing: r.closing, pv: r.pv_today }));

    const cols = [
        { key: "opening", label: "Opening Wealth" },
        columns.income && { key: "gross_income", label: "Gross Income", tone: "in" },
        columns.income && { key: "tax", label: "Tax", tone: "out" },
        ...(projection.income_stream_columns || []).flatMap((s) => [
            { key: `extra_inc:${s.id}`, label: s.name, tone: "in" },
            { key: `extra_tax:${s.id}`, label: `${s.name} Tax`, tone: "out" },
        ]),
        { key: "expenses", label: "Yearly Expenses", tone: "out" },
        ...(projection.emi_columns || []).map((e) => ({ key: `emi:${e.id}`, label: e.name, tone: "out" })),
        columns.upkeep && { key: "upkeep", label: "Upkeep", tone: "out" },
        columns.car && { key: "car", label: "Car Purchase", tone: "out" },
        columns.house && { key: "house", label: "House Purchase", tone: "out" },
        columns.misc && { key: "misc", label: "Miscellaneous", tone: "out" },
        columns.inheritance && { key: "inheritance", label: "Hereditary", tone: "in" },
        ...childCols.map((c) => ({ key: `child:${c.id}`, label: c.name, tone: "out" })),
        columns.higher_education && { key: "higher_education", label: "Higher Education", tone: "out" },
        columns.marriage && { key: "marriage", label: "Children's Marriage", tone: "out" },
        { key: "growth_rate", label: "Growth", fmt: (v) => `${v}%` },
        { key: "closing", label: "Closing Wealth", bold: true },
        { key: "pv_today", label: "PV in Today's Money" },
        { key: "risk_free_monthly", label: `Risk-Free Monthly Income` },
    ].filter(Boolean);

    const cell = (r, c) => {
        if (c.key.startsWith("emi:")) {
            const val = r.emis?.[c.key.slice(4)] || 0;
            return val === 0 ? "—" : formatINR(val);
        }
        if (c.key.startsWith("extra_inc:")) {
            const val = r.extra_income_gross?.[c.key.slice(10)] || 0;
            return val === 0 ? "—" : formatINR(val);
        }
        if (c.key.startsWith("extra_tax:")) {
            const val = r.extra_income_tax?.[c.key.slice(10)] || 0;
            return val === 0 ? "—" : formatINR(val);
        }
        const v = c.key.startsWith("child:") ? (r.children?.[c.key.slice(6)] || 0) : r[c.key];
        if (c.fmt) return c.fmt(v);
        return v === 0 && !c.bold && c.key !== "opening" ? "—" : formatINR(v);
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <Stat label="Starting Wealth" value={formatCompact(summary.starting_wealth)} testId="stat-starting-wealth" tip="The total current value of every investment you entered — the base your projection compounds from." />
                <Stat label="Portfolio Weighted Return" value={`${summary.weighted_return.toFixed(2)}%`} testId="stat-weighted-return" tip="Your portfolio's blended annual return, weighted by how much you have in each asset." />
                <Stat label={`Projected Wealth in ${projection.end_year}`} value={formatCompact(summary.final_wealth)} accent={summary.final_wealth < 0 ? "text-red-600" : ""} testId="stat-final-wealth" tip={`The nominal wealth you're projected to end with in ${projection.end_year}.`} />
                <Stat label="Projected Wealth in Present Value" value={formatCompact(summary.final_wealth_pv)} accent={summary.final_wealth_pv < 0 ? "text-red-600" : ""} testId="stat-final-pv" tip="What your projected end wealth is worth in today's money after discounting for inflation." />
                <Stat label="Lifestyle Sustainability Amount" value={formatCompact(summary.final_risk_free_monthly)} testId="stat-rf-monthly" tip="The amount you'd need today, invested risk-free, to cover your monthly expenses in the future." />
                <Stat label="Wealth Turns Negative" value={neg ? String(neg) : "Never"} accent={neg ? "text-red-600" : "text-emerald-700"} testId="stat-negative-year" tip="The first year your projected closing wealth falls below zero." />
            </div>

            {neg && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 flex items-center gap-3 text-sm font-body text-red-700" data-testid="negative-wealth-banner">
                    <AlertTriangle size={18} className="shrink-0" />
                    Projected wealth becomes negative in <strong>{neg}</strong>. Consider adjusting expenses, goals or growth assumptions.
                </div>
            )}

            <div className="rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8" data-testid="projection-chart-card">
                <h3 className="font-display text-lg font-semibold tracking-tight mb-1">Wealth over time</h3>
                <p className="text-sm text-oth-ink3 font-body mb-5">Closing wealth per year vs its value in today's purchasing power.</p>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                            <defs>
                                <linearGradient id="gClosing" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0B3B60" stopOpacity={0.25} />
                                    <stop offset="100%" stopColor="#0B3B60" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#8a8a84" stopOpacity={0.2} />
                                    <stop offset="100%" stopColor="#8a8a84" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e3dd" />
                            <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6b6b67" }} tickLine={false} axisLine={false} />
                            <YAxis tickFormatter={(v) => formatCompact(v)} tick={{ fontSize: 11, fill: "#6b6b67" }} width={72} tickLine={false} axisLine={false} />
                            <ChartTooltip formatter={(v, name) => [formatCompact(v), name === "closing" ? "Closing Wealth" : "PV in Today's Money"]} labelFormatter={(y) => `Year ${y}`} contentStyle={{ borderRadius: 12, border: "1px solid #d9d7d1", fontSize: 12 }} />
                            <Legend formatter={(v) => (v === "closing" ? "Closing Wealth" : "PV in Today's Money")} wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="closing" stroke="#0B3B60" strokeWidth={2} fill="url(#gClosing)" />
                            <Area type="monotone" dataKey="pv" stroke="#8a8a84" strokeWidth={1.5} strokeDasharray="5 4" fill="url(#gPv)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="rounded-3xl border border-oth-line/70 bg-white overflow-hidden" data-testid="projection-table-card">
                <div className="p-6 md:p-8 pb-4">
                    <h3 className="font-display text-lg font-semibold tracking-tight">Year-by-year projection</h3>
                    <p className="text-sm text-oth-ink3 font-body mt-1">
                        All figures in ₹. <span className="text-emerald-700">Green</span> columns are inflows, <span className="text-red-600">red</span> are outflows.
                    </p>
                </div>
                <div className="overflow-x-auto thin-scroll max-h-[560px] overflow-y-auto">
                    <table className="text-[13px] font-body border-collapse min-w-full" data-testid="projection-table">
                        <thead className="sticky top-0 z-20">
                            <tr className="bg-[#0A0A0A] text-white">
                                <th className={`${th} text-left sticky left-0 z-30 bg-[#0A0A0A]`}>Year</th>
                                {cols.map((c) => (
                                    <th key={c.key} className={`${th} ${c.tone === "in" ? "text-emerald-300" : c.tone === "out" ? "text-red-300" : ""}`}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.year} className={`border-b border-oth-line/40 ${r.closing < 0 ? "bg-red-50" : i % 2 ? "bg-[#fafaf8]" : "bg-white"}`} data-testid={`projection-row-${r.year}`}>
                                    <td className={`px-3 py-2 font-semibold sticky left-0 z-10 ${r.closing < 0 ? "bg-red-50" : i % 2 ? "bg-[#fafaf8]" : "bg-white"}`}>{r.year}</td>
                                    {cols.map((c) => (
                                        <td key={c.key} className={`${td} ${c.bold ? "font-semibold" : ""} ${(c.key === "closing" || c.key === "pv_today") && (c.key === "closing" ? r.closing : r.pv_today) < 0 ? "text-red-600 font-semibold" : c.tone === "in" && cell(r, c) !== "—" ? "text-emerald-700" : c.tone === "out" && cell(r, c) !== "—" ? "text-red-600" : ""}`}>
                                            {cell(r, c)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default function StepProjection({ scenarios, errors, buildPayload, planCode, planSaved, activeId }) {
    if (errors?.length) {
        return (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-6 md:p-8" data-testid="projection-errors">
                <div className="flex items-center gap-2 font-display font-semibold text-red-700 mb-3"><AlertTriangle size={18} /> Please fix these before we can project</div>
                <ul className="list-disc pl-5 space-y-1 text-sm font-body text-red-700">
                    {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
            </div>
        );
    }
    if (!scenarios || !scenarios.length) {
        return <div className="rounded-3xl border border-oth-line/70 bg-white p-10 text-center font-editorial italic text-oth-ink3" data-testid="projection-loading">Calculating your projection…</div>;
    }

    const multi = scenarios.length > 1;
    const active = scenarios.find((s) => s.id === activeId) || scenarios[0];

    return (
        <div className="space-y-6" data-testid="planner-step-projection">
            <ExportBar buildPayload={buildPayload} planCode={planCode} planSaved={planSaved} multi={multi} />
            {multi && <ComparisonCard scenarios={scenarios} />}
            {multi && (
                <div className="text-sm font-body text-oth-ink2 pl-1" data-testid="active-scenario-label">
                    Showing full projection for <strong>{active.name}</strong> — switch scenarios using the tabs above.
                </div>
            )}
            <ScenarioProjection projection={active.projection} />
        </div>
    );
}
